import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Download, 
  Calendar, 
  Filter, 
  Search, 
  Plus, 
  Trash2, 
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Users,
  Key,
  Store,
  Package,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import MockDataService from '../../services/MockDataService';

const LicenseManagement = () => {
  const [licenses, setLicenses] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPluginModal, setShowPluginModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [newLicense, setNewLicense] = useState({
    name: '',
    type: 'subscription',
    key: '',
    expiryDate: '',
    maxUsers: '',
    features: '',
    status: 'active'
  });

  useEffect(() => {
    loadLicenses();
    loadPlugins();
  }, []);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getLicenses();
      if (response.success) {
        setLicenses(response.data);
      }
    } catch (error) {
      console.error('Error loading licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlugins = async () => {
    try {
      const response = await MockDataService.getPluginMarketplace();
      if (response.success) {
        setPlugins(response.data);
      }
    } catch (error) {
      console.error('Error loading plugins:', error);
    }
  };

  const handleCreateLicense = async () => {
    try {
      const response = await MockDataService.createLicense(newLicense);
      if (response.success) {
        setLicenses([...licenses, response.data]);
        setShowCreateModal(false);
        setNewLicense({
          name: '',
          type: 'subscription',
          key: '',
          expiryDate: '',
          maxUsers: '',
          features: '',
          status: 'active'
        });
      }
    } catch (error) {
      console.error('Error creating license:', error);
    }
  };

  const handleViewLicense = (license) => {
    setSelectedLicense(license);
    setShowViewModal(true);
  };

  const handleViewPlugin = (plugin) => {
    setShowPluginModal(true);
  };

  const handleInstallPlugin = async (pluginId) => {
    try {
      const updatedPlugins = plugins.map(plugin => 
        plugin.id === pluginId 
          ? { ...plugin, status: 'installed', installedAt: new Date().toISOString() }
          : plugin
      );
      setPlugins(updatedPlugins);
    } catch (error) {
      console.error('Error installing plugin:', error);
    }
  };

  const handleRenewLicense = async (licenseId) => {
    try {
      const updatedLicenses = licenses.map(license => 
        license.id === licenseId 
          ? { ...license, expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() }
          : license
      );
      setLicenses(updatedLicenses);
    } catch (error) {
      console.error('Error renewing license:', error);
    }
  };

  const handleDeleteLicense = async (licenseId) => {
    try {
      const updatedLicenses = licenses.filter(license => license.id !== licenseId);
      setLicenses(updatedLicenses);
    } catch (error) {
      console.error('Error deleting license:', error);
    }
  };

  const downloadLicenseFile = (license) => {
    try {
      const content = JSON.stringify(license, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${license.name}-license.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading license:', error);
    }
  };

  const filteredLicenses = licenses.filter(license => {
    const matchesSearch = license.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         license.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || license.status === filterStatus;
    const matchesType = filterType === 'all' || license.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'green', icon: CheckCircle },
      expired: { color: 'red', icon: AlertCircle },
      suspended: { color: 'yellow', icon: Clock },
      trial: { color: 'blue', icon: Clock }
    };
    
    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
      <Badge color={config.color} className="flex items-center gap-1">
        <Icon size={12} />
        {status}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      subscription: { color: 'blue', icon: CreditCard },
      perpetual: { color: 'green', icon: Shield },
      trial: { color: 'yellow', icon: Clock },
      enterprise: { color: 'purple', icon: Users }
    };
    
    const config = typeConfig[type] || typeConfig.subscription;
    const Icon = config.icon;
    
    return (
      <Badge color={config.color} className="flex items-center gap-1">
        <Icon size={12} />
        {type}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">License Management</h1>
          <p className="text-gray-600">Manage system licenses and plugin marketplace</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={() => setShowPluginModal(true)}
            className="flex items-center gap-2"
          >
            <Store size={16} />
            Plugin Store
          </Button>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Add License
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Licenses</p>
              <p className="text-2xl font-bold text-gray-900">{licenses.length}</p>
            </div>
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Licenses</p>
              <p className="text-2xl font-bold text-green-600">
                {licenses.filter(l => l.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-bold text-yellow-600">
                {licenses.filter(l => l.status === 'expired').length}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-yellow-600" />
          </div>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Installed Plugins</p>
              <p className="text-2xl font-bold text-purple-600">
                {plugins.filter(p => p.status === 'installed').length}
              </p>
            </div>
            <Package className="h-8 w-8 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Search licenses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
            <option value="trial">Trial</option>
          </Select>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="subscription">Subscription</option>
            <option value="perpetual">Perpetual</option>
            <option value="trial">Trial</option>
            <option value="enterprise">Enterprise</option>
          </Select>
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => setShowFilterModal(!showFilterModal)}
          >
            <Filter size={16} />
            More Filters
          </Button>
        </div>
      </Card>

      {/* Licenses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredLicenses.map((license) => (
          <Card key={license.id} className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">{license.name}</h3>
                <p className="text-sm text-gray-600 mb-3">{license.description}</p>
              </div>
              <div className="flex gap-2">
                {getStatusBadge(license.status)}
                {getTypeBadge(license.type)}
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar size={14} />
                <span>Expires: {new Date(license.expiryDate).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users size={14} />
                <span>Max Users: {license.maxUsers}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Key size={14} />
                <span>Key: {license.key.substring(0, 8)}...</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewLicense(license)}
                className="flex-1"
              >
                <Eye size={14} />
              </Button>
              {license.status === 'expired' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRenewLicense(license.id)}
                  className="flex-1"
                >
                  <RefreshCw size={14} />
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteLicense(license.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Create License Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New License"
        size="lg"
      >
        <div className="space-y-6">
            {/* Basic Information Section */}
            <div className="space-y-5">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                <Key className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newLicense.name}
                  onChange={(e) => setNewLicense({ ...newLicense, name: e.target.value })}
                  placeholder="e.g., HelpDesk Pro Enterprise"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1.5">Enter a descriptive name for this license</p>
              </div>
              
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    License Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newLicense.type}
                    onChange={(e) => setNewLicense({ ...newLicense, type: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900"
                  >
                    <option value="subscription">Subscription</option>
                    <option value="perpetual">Perpetual</option>
                    <option value="trial">Trial</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Users <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newLicense.maxUsers}
                    onChange={(e) => setNewLicense({ ...newLicense, maxUsers: e.target.value })}
                    placeholder="e.g., 100"
                    type="number"
                    min="1"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">Maximum number of users allowed</p>
                </div>
              </div>
            </div>

            {/* License Details Section */}
            <div className="space-y-5 pt-5 border-t border-gray-200">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                <Shield className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">License Details</h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Key <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newLicense.key}
                  onChange={(e) => setNewLicense({ ...newLicense, key: e.target.value })}
                  placeholder="Enter license key (e.g., HD-PRO-XXXX-XXXX-XXXX)"
                  className="w-full font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1.5">Enter the unique license key provided by the vendor</p>
              </div>

              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <Input
                    value={newLicense.expiryDate}
                    onChange={(e) => setNewLicense({ ...newLicense, expiryDate: e.target.value })}
                    type="date"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1.5">Leave empty for perpetual licenses</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={newLicense.status}
                    onChange={(e) => setNewLicense({ ...newLicense, status: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-gray-900"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="space-y-5 pt-5 border-t border-gray-200">
              <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                <Package className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Features</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Features
                </label>
                <Textarea
                  value={newLicense.features}
                  onChange={(e) => setNewLicense({ ...newLicense, features: e.target.value })}
                  placeholder="Enter license features (one per line or comma-separated)&#10;e.g., Advanced Analytics, API Access, Priority Support"
                  rows={4}
                  className="w-full resize-none"
                />
                <p className="text-xs text-gray-500 mt-1.5">List the features included in this license</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-2.5 font-medium min-w-[100px]"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateLicense}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm min-w-[140px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add License
              </Button>
            </div>
        </div>
      </Modal>

      {/* View License Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedLicense?.name}
        size="lg"
      >
        {selectedLicense && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                {getStatusBadge(selectedLicense.status)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                {getTypeBadge(selectedLicense.type)}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <p className="text-gray-900">{selectedLicense.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Key
                </label>
                <p className="text-gray-900 font-mono">{selectedLicense.key}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Users
                </label>
                <p className="text-gray-900">{selectedLicense.maxUsers}</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date
              </label>
              <p className="text-gray-900">
                {new Date(selectedLicense.expiryDate).toLocaleDateString()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Features
              </label>
              <p className="text-gray-900">{selectedLicense.features}</p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </Button>
              <Button 
                className="flex items-center gap-2"
                onClick={() => downloadLicenseFile(selectedLicense)}
              >
                <Download size={16} />
                Download License
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Plugin Store Modal */}
      <Modal
        isOpen={showPluginModal}
        onClose={() => setShowPluginModal(false)}
        title="Plugin Marketplace"
        size="full"
      >
        <div className="flex flex-col">
          {/* Search and Filter Bar */}
          <div className="mb-6 pb-4 border-b border-gray-200 flex-shrink-0 -mx-6 -mt-4 px-6 pt-0 bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search plugins by name or description..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilterModal(!showFilterModal)}>
                <Filter size={16} className="mr-2" />
                Filter
              </Button>
            </div>
          </div>

          {/* Plugin Grid with Scrollbar */}
          <div className="overflow-y-auto overflow-x-hidden -mx-6 px-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 py-2">
              {plugins
                .filter(plugin => 
                  plugin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  plugin.description.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((plugin) => (
                <Card 
                  key={plugin.id} 
                  className="p-5 hover:shadow-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 group bg-white"
                >
                  {/* Header with Badge */}
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="font-semibold text-gray-900 text-base group-hover:text-blue-600 transition-colors leading-tight">
                      {plugin.name}
                    </h4>
                    <Badge 
                      variant={plugin.status === 'installed' ? 'default' : 'secondary'}
                      className={`${
                        plugin.status === 'installed' 
                          ? 'bg-green-100 text-green-800 border-green-200' 
                          : 'bg-blue-100 text-blue-800 border-blue-200'
                      } border font-medium text-xs px-2.5 py-1`}
                    >
                      {plugin.status === 'installed' ? 'Installed' : 'Available'}
                    </Badge>
                  </div>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3 min-h-[3.5rem] leading-relaxed">
                    {plugin.description}
                  </p>
                  
                  {/* Price and Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-gray-900">${plugin.price}</span>
                      <span className="text-xs text-gray-500 font-medium">/month</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewPlugin(plugin)}
                        className="h-9 w-9 p-0 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                        title="View Details"
                      >
                        <Eye size={16} className="text-gray-600" />
                      </Button>
                      {plugin.status !== 'installed' && (
                        <Button
                          size="sm"
                          onClick={() => handleInstallPlugin(plugin.id)}
                          className="h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-sm"
                        >
                          Install
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            
            {/* Empty State */}
            {plugins.filter(plugin => 
              plugin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              plugin.description.toLowerCase().includes(searchTerm.toLowerCase())
            ).length === 0 && (
              <div className="text-center py-16">
                <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium mb-1">No plugins found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search terms</p>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LicenseManagement;
